import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import {useGetStudentFiltersQuery, useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {
    useGetFormEmployeeAssignmentsQuery,
    useGetFormStudentsAssignmentsQuery
} from '../../../../api/services/formAssignmentsService';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import React, {useEffect, useMemo, useState} from 'react';
import SettingsSectionBase, {SettingsSection} from './SettingsSection';
import {Box, Checkbox, FormControlLabel, Typography} from '@mui/material';
import EmailsInput from '../../../../shared-components/EmailsInput';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddStudentGroupDialog from './AddStudentGroupDialog';
import {mergeArraysByKey, mergeArraysByKeys} from '../../../../utility/mergeArraysByKey';
import {validateAccessLevel, validateGroupFilters} from '../../../../utility/validationUtil';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import Divider from '@mui/material/Divider';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';

/* eslint-disable react/prop-types */

const options = {
    [FORM_ASSIGNMENT_TYPE.students]: {
        id: FORM_ASSIGNMENT_TYPE.students,
        label: 'Student Assignments',
        icon: <Groups2OutlinedIcon fontSize="small" />,
    },
    [FORM_ASSIGNMENT_TYPE.employees]: {
        id: FORM_ASSIGNMENT_TYPE.employees,
        label: 'Employee Assignments',
        icon: <AssignmentIndOutlinedIcon fontSize="small" />,
    },
};

const getGroupFilterStatusInfo = (isExcluded) =>
    ({
        label: isExcluded ? 'Excluded' : 'Included',
        color: isExcluded ? 'red' : 'green'
    });

const RegularFormSettings = (
    {
        audience,
        formId,
        settings,
        strictRecipientsFiltering,
        isFormLive,
        externalUsers,
        accessLevel,
        onSettingsChange,
        onSetStrictRecipientsFiltering
    }) => {
    const {
        groupFilters,
        studentFilters,
        applicationFilters,
        enquiryFilters,
        employeeFilters,
        externalUserEmailAssignments,
        assignmentType
    } = settings;
    const [originalSettings, setOriginalSettings] = useState({
        groupFilters: [],
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: [],
        employeeFilters: [],
        externalUserEmailAssignments: [],
        assignmentType: FORM_ASSIGNMENT_TYPE.none
    });
    const fetchEmployeeAssignments =
        audience === FORM_AUDIENCE_TYPE.employees ||
        audience === FORM_AUDIENCE_TYPE.externalUsers;
    const fetchStudentsAssignments =
        audience === FORM_AUDIENCE_TYPE.students ||
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
        audience === FORM_AUDIENCE_TYPE.previousSchool ||
        audience === FORM_AUDIENCE_TYPE.externalUsers;
    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: !audience || audience === FORM_AUDIENCE_TYPE.employees});
    const {data} = useGetGroupFiltersQuery({}, {skip: !audience});
    const {
        data: studentFormAssignmentData,
        isSuccess: isFetchStudentFormAssignmentSuccess
    } = useGetFormStudentsAssignmentsQuery({formId}, {
        skip: !fetchStudentsAssignments && !(
            originalSettings.studentFilters.length ||
            originalSettings.applicationFilters.length ||
            originalSettings.enquiryFilters.length ||
            originalSettings.groupFilters)
    });
    const {
        data: employeeFormAssignmentData,
        isSuccess: isFetchEmployeeFormAssignmentSuccess
    } = useGetFormEmployeeAssignmentsQuery({formId}, {
        skip: !fetchEmployeeAssignments && !(originalSettings.employeeFilters.length || originalSettings.groupFilters)
    });
    const assignments = studentFormAssignmentData ?? employeeFormAssignmentData ?? {
        personAssignments: [],
        groupFilters: [],
    };
    const {
        data: studentsData,
    } = useGetStudentFiltersQuery({
        body: {
            studentIds: studentFormAssignmentData?.personAssignments?.map(x => x.personId)
        }
    }, {skip: !audience || !studentFormAssignmentData || studentFormAssignmentData.personAssignments.length === 0});

    useEffect(() => {
        if (assignments.groupFilters) {
            const groupFilters = assignments.groupFilters.map(x => ({
                id: x.filterId,
                displayName: x.displayName,
                groupId: x.id,
                excluded: x.excluded
            }));

            onSettingsChange(prev => ({
                ...prev,
                groupFilters,
                validation: {
                    ...validateGroupFilters(groupFilters),
                    ...validateAccessLevel(accessLevel, groupFilters)
                }
            }));
            setOriginalSettings(prev => ({...prev, groupFilters}));
        }
    }, [assignments.groupFilters]);

    useEffect(() => {
        if (!isFetchStudentFormAssignmentSuccess) return;
        if (!studentFormAssignmentData || !studentsData?.length) return;

        const studentsMap = studentsData.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {});
        const filters = studentFormAssignmentData.personAssignments.reduce((acc, item) => {
            const pupil = studentsMap[item.personId];

            if (!pupil) {
                return acc;
            }

            const filterItem = {
                id: item.personId,
                label: item.displayName,
                assignmentId: item.id
            };

            if (pupil.isApplicant) {
                acc.applicationFilters.push(filterItem);
            } else if (pupil.isStudent) {
                acc.studentFilters.push(filterItem);
            } else if (pupil.isEnquiry) {
                acc.enquiryFilters.push(filterItem);
            }

            return acc;
        }, {studentFilters: [], applicationFilters: [], enquiryFilters: []});

        onSettingsChange(prev => ({...prev, ...filters, assignmentType: FORM_ASSIGNMENT_TYPE.students}));
        setOriginalSettings(prev => ({...prev, ...filters, assignmentType: FORM_ASSIGNMENT_TYPE.students}));
    }, [isFetchStudentFormAssignmentSuccess, studentFormAssignmentData, studentsData]);

    useEffect(() => {
        if (!isFetchEmployeeFormAssignmentSuccess) return;
        if (!employeeFormAssignmentData?.personAssignments?.length) return;

        const employeeFilters = employeeFormAssignmentData.personAssignments.map(item => ({
            id: item.personId,
            assignmentId: item.id,
            label: item.displayName
        }));

        onSettingsChange(prev => ({...prev, employeeFilters, assignmentType: FORM_ASSIGNMENT_TYPE.employees}));
        setOriginalSettings(prev => ({...prev, employeeFilters, assignmentType: FORM_ASSIGNMENT_TYPE.employees}));
    }, [isFetchEmployeeFormAssignmentSuccess, employeeFormAssignmentData]);

    useEffect(() => {
        if (externalUsers) {
            onSettingsChange(prev => ({
                ...prev,
                externalUserEmailAssignments: externalUsers
            }));
            setOriginalSettings(prev => ({
                ...prev,
                externalUserEmailAssignments: externalUsers
            }));
        }
    }, [externalUsers]);

    const handleChangeGroupFilters = filters =>
        onSettingsChange(prev => ({
            ...prev,
            groupFilters: filters,
            validation: {
                ...validateGroupFilters(filters),
                ...validateAccessLevel(accessLevel, filters)
            }
        }));

    const handleStudentFiltersChange = value =>
        onSettingsChange(prev => ({
            ...prev,
            ...value
        }));

    const handleEmployeeFiltersChange = filters =>
        onSettingsChange(prev => ({
            ...prev,
            employeeFilters: filters
        }));

    const handleAssignmentTypeChange = (e) => {
        const newType = e.target.value;

        onSettingsChange((prev) => {
            const nextFilters = {
                studentFilters: mergeArraysByKey([], originalSettings.studentFilters),
                applicationFilters: mergeArraysByKey([], originalSettings.applicationFilters),
                enquiryFilters: mergeArraysByKey([], originalSettings.enquiryFilters),
                employeeFilters: mergeArraysByKey([], originalSettings.employeeFilters),
                groupFilters: mergeArraysByKeys([], originalSettings.groupFilters, ['id', 'excluded']),
            };

            return {...prev, assignmentType: newType, ...nextFilters};
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {
                audience === FORM_AUDIENCE_TYPE.externalUsers
                    ? <SettingsSectionBase
                        header="External users"
                        headerIcon={<PeopleOutlineOutlinedIcon fontSize="small"/>}
                        subHeader="Type one or more user emails"
                    >
                        <SettingsSection>
                            <EmailsInput
                                values={externalUserEmailAssignments.filter(x => !x.isDeleted)}
                                onChange={emails => onSettingsChange(prev => ({
                                    ...prev,
                                    externalUserEmailAssignments: mergeArraysByKey(
                                        emails,
                                        originalSettings.externalUserEmailAssignments,
                                        'email')
                                }))}
                                onBlur={email => onSettingsChange(prev => ({
                                    ...prev,
                                    externalUserEmailAssignments: mergeArraysByKey(
                                        [...settings.externalUserEmailAssignments, {email}],
                                        originalSettings.externalUserEmailAssignments,
                                        'email')
                                }))}
                            />
                        </SettingsSection>
                        <Divider sx={{mt: 0, mb: 1}}/>
                        <SettingsSection>
                            <FormControl
                                variant="outlined"
                                disabled={settings && ([
                                    ...settings.groupFilters,
                                    ...settings?.employeeFilters,
                                    ...settings?.studentFilters,
                                    ...settings?.applicationFilters,
                                    ...settings?.enquiryFilters,
                                ].filter(x => x.isDeleted == null || !x.isDeleted).length > 0)}
                            >
                                <InputLabel id="assignment-type-label">Assignment Type</InputLabel>
                                <Select
                                    labelId="assignment-type-label"
                                    value={assignmentType}
                                    onChange={handleAssignmentTypeChange}
                                    label="Assignment Type"
                                    renderValue={(selected) => {
                                        const {icon, label} = options[selected];
                                        return (
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {icon}
                                                {label}
                                            </Box>
                                        );
                                    }}
                                >
                                    {Object.entries(options).map(([key, {id, label, icon}]) =>
                                        (
                                            <MenuItem key={key} value={id}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {icon}
                                                    {label}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </SettingsSection>
                    </SettingsSectionBase>
                    : null
            }
            {
                assignmentType === FORM_ASSIGNMENT_TYPE.employees && (
                    audience === FORM_AUDIENCE_TYPE.employees ||
                    audience === FORM_AUDIENCE_TYPE.externalUsers)
                    ? <EmployeeFiltersSelector
                        originalSettings={originalSettings}
                        groupFilterModules={data?.modules}
                        groupFilters={groupFilters}
                        employeeFilters={employeeFilters}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        isFormLive={isFormLive}
                        onChangeGroupFilters={handleChangeGroupFilters}
                        onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                        onSettingsChange={onSettingsChange}
                        onEmployeeFiltersChange={handleEmployeeFiltersChange}
                    />
                    : null
            }
            {
                assignmentType === FORM_ASSIGNMENT_TYPE.students && (audience === FORM_AUDIENCE_TYPE.students ||
                    audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    audience === FORM_AUDIENCE_TYPE.previousSchool ||
                    audience === FORM_AUDIENCE_TYPE.externalUsers)
                    ? <StudentFiltersSelector
                        originalSettings={originalSettings}
                        groupFilterModules={data?.modules.filter(m => m.membersType !== GROUP_MEMBERS_TYPE.employee)
                            .map(({
                                      filters,
                                      ...rest
                                  }) => ({
                                filters: filters.filter(filter => filter.membersType !== GROUP_MEMBERS_TYPE.employee),
                                ...rest
                            })) || []}
                        groupFilters={groupFilters}
                        studentFilters={studentFilters}
                        applicationFilters={applicationFilters}
                        enquiryFilters={enquiryFilters}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        isFormLive={isFormLive}
                        onChangeGroupFilters={handleChangeGroupFilters}
                        onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                        onStudentFiltersChange={handleStudentFiltersChange}
                        schoolInfo={schoolInfo}
                    />
                    : null
            }
        </Box>
    );
};

function StudentFiltersSelector(
    {
        originalSettings,
        groupFilterModules,
        groupFilters,
        studentFilters,
        applicationFilters,
        enquiryFilters,
        strictRecipientsFiltering,
        isFormLive,
        onChangeGroupFilters,
        onSetStrictRecipientsFiltering,
        onStudentFiltersChange,
        schoolInfo
    }) {
    return (
        <SettingsSectionBase
            header="Student Assignments"
            headerIcon={<Groups2OutlinedIcon fontSize="small"/>}
            subHeader="Select student groups and individual students for assignments"

        >
            <GroupFiltersTable
                groupFilters={groupFilters}
                groupFilterModules={groupFilterModules}
                originalSettings={originalSettings}
                strictRecipientsFiltering={strictRecipientsFiltering}
                onChangeGroupFilters={onChangeGroupFilters}
                onChangeStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                isFormLive={isFormLive}
                header="Student Groups"
            />
            <SettingsSection>
                <Typography variant="subtitle1" fontWeight="bold">Select Individual Student</Typography>
                <PaginatedAutocomplete
                    textFieldProps={{
                        label: 'Applications',
                        placeholder: 'Enter name, surname, or full name',
                        required: true,
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{
                        queryParams: {
                            pupilType: 'applicant'
                        }
                    }}
                    getOptionLabel={(option) => option.label}
                    value={applicationFilters.filter(filter => !filter.isDeleted)}
                    onChange={(e, newValue) => onStudentFiltersChange({applicationFilters: mergeArraysByKey(newValue, originalSettings.applicationFilters)})}
                />
                <PaginatedAutocomplete
                    textFieldProps={{
                        label: 'Students',
                        placeholder: 'Enter name, surname, or full name',
                        required: true,
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{
                        queryParams: {
                            pupilType: 'student'
                        }
                    }}
                    getOptionLabel={(option) => option.label}
                    value={studentFilters.filter(filter => !filter.isDeleted)}
                    onChange={(e, newValue) => onStudentFiltersChange({studentFilters: mergeArraysByKey(newValue, originalSettings.studentFilters)})}
                />
                {
                    schoolInfo == null || schoolInfo.disableEnquiriesSync
                        ? null
                        : <PaginatedAutocomplete
                            textFieldProps={{
                                label: 'Enquiries',
                                placeholder: 'Enter name, surname, or full name',
                                required: true,
                            }}
                            fetchFn={useLazyGetStudentFiltersQuery}
                            fetchFnProps={{
                                queryParams: {
                                    pupilType: 'enquiry'
                                }
                            }}
                            getOptionLabel={(option) => option.label}
                            value={enquiryFilters.filter(filter => !filter.isDeleted)}
                            onChange={(e, newValue) => onStudentFiltersChange({enquiryFilters: mergeArraysByKey(newValue, originalSettings.enquiryFilters)})}
                        />
                }
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function EmployeeFiltersSelector(
    {
        originalSettings,
        groupFilterModules,
        groupFilters,
        employeeFilters,
        strictRecipientsFiltering,
        isFormLive,
        onChangeGroupFilters,
        onSetStrictRecipientsFiltering,
        onEmployeeFiltersChange
    }) {
    const filteredGroupFilterModules = useMemo(() => groupFilterModules
            ?.filter((group) =>
                group.name === 'EMPLOYEES' ||
                group.name?.toLowerCase().includes('school group') ||
                group.name?.toLowerCase().includes('my group'))
            .map(({filters, ...rest}) => ({
                filters: filters.filter(filter => filter.membersType === GROUP_MEMBERS_TYPE.employee),
                ...rest
            })) || [],
        [groupFilterModules?.length]);

    return (
        <>
            <SettingsSectionBase
                header="Employee Assignments"
                headerIcon={<AssignmentIndOutlinedIcon fontSize="small"/>}
                subHeader="Select employee groups and individual employees for assignments."
            >
                <GroupFiltersTable
                    groupFilters={groupFilters}
                    groupFilterModules={filteredGroupFilterModules}
                    originalSettings={originalSettings}
                    strictRecipientsFiltering={strictRecipientsFiltering}
                    onChangeStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                    onChangeGroupFilters={onChangeGroupFilters}
                    header="Select Employee Groups"
                    isFormLive={isFormLive}
                />
                <SettingsSection>
                    <Typography variant="subtitle1" fontWeight="bold">Select Individual Employee</Typography>
                    <PaginatedAutocomplete
                        textFieldProps={{
                            label: 'Employees',
                            placeholder: 'Enter name, surname, or full name',
                            required: true,
                        }}
                        fetchFn={useLazyGetAllEmployeeProfilesQuery}
                        getOptionLabel={(option) => option.label || option.preferredForename + ' ' + option.preferredSurname}
                        value={employeeFilters.filter(filter => !filter.isDeleted)}
                        onChange={(e, newValue) => onEmployeeFiltersChange(mergeArraysByKey(newValue, originalSettings.employeeFilters))}
                    />
                </SettingsSection>
            </SettingsSectionBase>
        </>
    );
}

function GroupFiltersTable(
    {
        originalSettings,
        groupFilters,
        groupFilterModules,
        onChangeGroupFilters,
        onChangeStrictRecipientsFiltering,
        isFormLive,
        strictRecipientsFiltering,
        header
    }) {
    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const data = groupFilters.filter(filter => !filter.isDeleted);

    return (
        <>
            <SettingsSection>
                <Stack justifyContent="space-between" flexDirection="row" alignItems="center" gap={2}>
                    <Typography variant="subtitle1" fontWeight="bold">{header}</Typography>
                    <Button
                        sx={{
                            minWidth: 160
                        }}
                        variant="outlined"
                        onClick={() => setOpenAddGroupDialog(true)}
                        startIcon={<AddOutlinedIcon/>}
                    >Add Group</Button>
                </Stack>
            </SettingsSection>
            {
                data.length
                    ? <SettingsSection>
                        <AdmicityTableV2
                            columns={[
                                {
                                    accessorKey: 'displayName',
                                    header: 'Group Name',
                                    grow: true,
                                },
                                {
                                    accessorKey: 'excluded',
                                    header: 'Status',
                                    size: 100,
                                    grow: false,
                                    Cell: ({cell}) => <AdmicityStatus {...getGroupFilterStatusInfo(cell.getValue())}/>
                                }
                            ]}
                            data={groupFilters.filter(filter => !filter.isDeleted)}
                            tableProps={{
                                layoutMode: 'grid',
                                enableRowActions: true,
                                enableSorting: false,
                                enableFilters: false,
                                enableHiding: false,
                                enableFullScreenToggle: false,
                                enableColumnResizing: false,
                                enableBottomToolbar: false,
                                enableTopToolbar: false,
                                displayColumnDefOptions: {
                                    'mrt-row-actions': {
                                        size: 50,
                                        grow: false,
                                    },
                                },
                                muiTableBodyProps: {
                                    sx: {
                                        '& :last-child td': {
                                            borderBottom: 0,
                                        },
                                    }
                                },
                                muiTablePaperProps: {
                                    variant: 'outlined',
                                    elevation: 0,
                                    sx: {
                                        flexDirection: 'column',
                                        flexGrow: 1
                                    }
                                },
                                muiTableContainerProps: {
                                    sx: {
                                        maxHeight: 350,
                                        minHeight: 'auto',
                                        border: 'none'
                                    }
                                },
                                renderRowActions: ({row}) => <IconButton
                                    onClick={() => {
                                        const filters = groupFilters.filter(filter => row.original.id !== filter.id);
                                        const newFilters = mergeArraysByKeys(filters, originalSettings.groupFilters, ['id', 'excluded']);

                                        onChangeGroupFilters(newFilters);
                                    }}>
                                    <DeleteOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            }}
                        />
                    </SettingsSection>
                    : null
            }
            {
                data.length
                    ? <FormControlLabel
                        sx={{
                            gap: 1
                        }}
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginLeft: 1
                                }}
                                checked={strictRecipientsFiltering}
                                onChange={async (e) =>
                                    await onChangeStrictRecipientsFiltering(e.target.checked)
                                }
                                disabled={isFormLive}
                            />
                        }
                        label="Must appear in all these groups"
                    />
                    : null
            }
            <AddStudentGroupDialog
                open={openAddGroupDialog}
                groupFilters={groupFilterModules}
                onClose={() => setOpenAddGroupDialog(false)}
                onChange={(filter => {
                    const newFilter = {
                        id: filter.filterId,
                        displayName: filter.paths.map(x => x.name).join(' - '),
                        excluded: filter.excluded
                    };
                    const filters = [...groupFilters];
                    const filterIndex = filters.findIndex(x => x.id === newFilter.id);

                    if (filterIndex >= 0) {
                        filters[filterIndex] = newFilter;
                    } else {
                        filters.push(newFilter);
                    }

                    const newFilters = mergeArraysByKeys(filters, originalSettings.groupFilters, ['id', 'excluded']);

                    onChangeGroupFilters(newFilters);
                })}
            />
        </>
    );
}

export default RegularFormSettings;